import * as i0 from '@angular/core';
import { Pipe, NgModule } from '@angular/core';
import * as i1 from '@rx-angular/cdk/render-strategies';
import { strategyHandling } from '@rx-angular/cdk/render-strategies';
import { createTemplateNotifier } from '@rx-angular/cdk/notifications';
import { filter, tap, withLatestFrom, switchMap } from 'rxjs/operators';
import { timeoutSwitchMapWith } from '@rx-angular/cdk/internals/core';

/**
 * @Pipe PushPipe
 *
 * @description
 *
 * The push pipe serves as a drop-in replacement for angulars built-in async pipe.
 * Just like the *rxLet Directive, it leverages a
 * [RenderStrategy](https://github.com/rx-angular/rx-angular/blob/main/libs/cdk/docs/render-strategies/README.md)
 *   under the hood which takes care of optimizing the ChangeDetection of your component. The rendering behavior can be
 *   configured per PushPipe instance using either a strategy name or provide a
 * `RxComponentInput` config.
 *
 * Usage in the template
 *
 * ```html
 * <hero-search [term]="searchTerm$ | push"> </hero-search>
 * <hero-list-component [heroes]="heroes$ | push"> </hero-list-component>
 * ```
 *
 * Using different strategies
 *
 * ```html
 * <hero-search [term]="searchTerm$ | push: 'immediate'"> </hero-search>
 * <hero-list-component [heroes]="heroes$ | push: 'normal'"> </hero-list-component>
 * ```
 *
 * Provide a config object
 *
 * ```html
 * <hero-search [term]="searchTerm$ | push: { strategy: 'immediate' }"> </hero-search>
 * <hero-list-component [heroes]="heroes$ | push: { strategy: 'normal' }"> </hero-list-component>
 * ```
 *
 * Other Features:
 *
 * - lazy rendering (see
 *  [LetDirective](https://github.com/rx-angular/rx-angular/tree/main/libs/template/docs/api/let-directive.md))
 * - Take observables or promises, retrieve their values and render the value to the template
 * - a unified/structured way of handling null, undefined or error
 * - distinct same values in a row skip not needed re-renderings
 *
 * @usageNotes
 *
 * ```html
 * {{observable$ | push}}
 * <ng-container *ngIf="observable$ | push as o">{{o}}</ng-container>
 * <component [value]="observable$ | push"></component>
 * ```
 *
 * @publicApi
 */
class PushPipe {
    constructor(strategyProvider, cdRef, ngZone) {
        this.strategyProvider = strategyProvider;
        this.cdRef = cdRef;
        this.ngZone = ngZone;
        /** @internal */
        this.templateObserver = createTemplateNotifier();
        /** @internal */
        this.strategyHandler = strategyHandling(this.strategyProvider.primaryStrategy, this.strategyProvider.strategies);
        this.subscription = this.handleChangeDetection();
    }
    transform(potentialObservable, config, renderCallback) {
        this._renderCallback = renderCallback;
        if (config) {
            if (isRxComponentInput(config)) {
                this.strategyHandler.next(config.strategy);
                this._renderCallback = config.renderCallback;
                // set fallback if patchZone is not set
                this.setPatchZone(config.patchZone);
            }
            else {
                this.strategyHandler.next(config);
            }
        }
        this.templateObserver.next(potentialObservable);
        return this.renderedValue;
    }
    /** @internal */
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /** @internal */
    setPatchZone(patch) {
        const doPatch = patch == null ? this.strategyProvider.config.patchZone : patch;
        this.patchZone = doPatch ? this.ngZone : false;
    }
    /** @internal */
    handleChangeDetection() {
        const scope = this.cdRef.context;
        return this.templateObserver.values$
            .pipe(filter((n) => n.kind === "suspense" /* Suspense */ ||
            n.kind === "next" /* Next */), tap((notification) => {
            this.renderedValue = notification.value;
        }), withLatestFrom(this.strategyHandler.strategy$), switchMap(([notification, strategy]) => this.strategyProvider
            .schedule(() => {
            strategy.work(this.cdRef, scope);
        }, {
            scope,
            strategy: strategy.name,
            patchZone: this.patchZone,
        })
            .pipe(timeoutSwitchMapWith(), tap(() => { var _a; return (_a = this._renderCallback) === null || _a === void 0 ? void 0 : _a.next(notification.value); }))))
            .subscribe();
    }
}
/** @nocollapse */ PushPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PushPipe, deps: [{ token: i1.RxStrategyProvider }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Pipe });
/** @nocollapse */ PushPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PushPipe, name: "push", pure: false });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PushPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'push', pure: false }]
        }], ctorParameters: function () { return [{ type: i1.RxStrategyProvider }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }]; } });
// https://eslint.org/docs/rules/no-prototype-builtins
const hasOwnProperty = Object.prototype.hasOwnProperty;
function isRxComponentInput(value) {
    return (value != null &&
        (hasOwnProperty.call(value, 'strategy') ||
            hasOwnProperty.call(value, 'renderCallback') ||
            hasOwnProperty.call(value, 'patchZone')));
}

class PushModule {
}
/** @nocollapse */ PushModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PushModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ PushModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PushModule, declarations: [PushPipe], exports: [PushPipe] });
/** @nocollapse */ PushModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PushModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PushModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PushPipe],
                    exports: [PushPipe],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PushModule, PushPipe };
